import type { ReactNode } from 'react';
import { ChevronDown, ChevronRight } from '../../../../icons';
import { formatBytes } from '../../../../lib/attachments';
import { dayLabelParts, type AttachmentDayGroup } from '../../../../lib/attachment-groups';
import { tChat } from '../../../../lib/chat-i18n';

/**
 * 일자 아코디언 그룹 (chat-media-date-groups — 2026-08-07).
 *
 * 헤더 = chevron + 일자 라벨 + 건수 + 총 용량. 클릭하면 그 날짜의 내역이 펼쳐진다.
 * **접힌 그룹은 바디를 아예 렌더하지 않는다**(CSS 숨김이 아니라 조건부 렌더) — 이미지 탭에서
 * 접힌 일자가 presign 요청을 만들지 않게 하는 것이 요점이다.
 *
 * 레포에 범용 accordion 프리미티브가 없어 `components/SidebarGroup` 의 관용
 * (chevron + `aria-expanded` + `aria-controls`)만 따르고 마크업은 신규로 둔다.
 */

/**
 * 일자 라벨 — 채팅 본문의 날짜 구분선과 **같은 문구**가 나오도록 기존 `chat.list.day.*` 키를 재사용한다.
 * (`MessageList.tsx` 의 `formatDayLabel` 은 모듈 로컬 비-export 라 그 파일을 건드리지 않기 위해
 *  `dayLabelParts` 라는 데이터 경유로 같은 결과를 만든다.)
 */
export function formatDayGroupLabel(dayKey: string, nowMs?: number): string {
  const p = dayLabelParts(dayKey, nowMs);
  if (p.kind === 'unknown') return tChat('chat.media.day_unknown');
  const md = tChat('chat.list.day.md_ko', { month: p.month, day: p.day });
  if (p.kind === 'today') return tChat('chat.list.day.today', { md });
  if (p.kind === 'yesterday') return tChat('chat.list.day.yesterday', { md });
  if (p.kind === 'ymd') return tChat('chat.media.day_ymd', { year: p.year, month: p.month, day: p.day });
  return md;
}

interface MediaDayGroupProps {
  group: AttachmentDayGroup;
  expanded: boolean;
  onToggle: (dayKey: string) => void;
  children: ReactNode;
}

export function MediaDayGroup({ group, expanded, onToggle, children }: MediaDayGroupProps) {
  const label = formatDayGroupLabel(group.dayKey);
  const bodyId = `chat-media-day-${group.dayKey || 'unknown'}`;
  const Chevron = expanded ? ChevronDown : ChevronRight;

  return (
    <section className="chat-media-day">
      <button
        type="button"
        className="chat-media-day-header"
        onClick={() => onToggle(group.dayKey)}
        aria-expanded={expanded}
        aria-controls={bodyId}
        aria-label={tChat(expanded ? 'chat.media.day_collapse_aria' : 'chat.media.day_expand_aria', { label })}
      >
        <Chevron size={13} className="chat-media-day-chevron" aria-hidden />
        <span className="chat-media-day-label">{label}</span>
        <span className="chat-media-day-count">{group.items.length}</span>
        <span className="chat-media-day-size">{formatBytes(group.totalBytes)}</span>
      </button>
      {expanded && (
        <div className="chat-media-day-body" id={bodyId} role="region" aria-label={label}>
          {children}
        </div>
      )}
    </section>
  );
}
